import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Check, Minus, Plus, ChevronRight, ArrowUpRight, Loader2, Play, Pause, Volume2, VolumeX, Maximize, ShieldCheck } from 'lucide-react';
import { useUserAuth } from '../context/UserAuthContext';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';
import userApi from '../services/userApi';
import config from '../../config/env';

// Video Player Component
const VideoPlayer = ({ src, poster, title, id }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const videoRef = useRef(null);

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMute = () => {
    const video = videoRef.current;
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    const video = videoRef.current;
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
    }
  };

  if (!src) {
    return (
      <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="w-16 h-16 rounded-full border-2 border-white/20 flex items-center justify-center mb-4 group-hover:border-accent-color/50 transition-colors">
            <Play className="h-6 w-6 text-white/40 group-hover:text-accent-color transition-colors ml-1" />
          </div>
          <p className="text-white/40 text-sm font-mono tracking-wide">{title || 'VIDEO COMING SOON'}</p>
        </div>
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
      <video
        ref={videoRef}
        id={id}
        className="w-full h-full object-contain bg-black"
        poster={poster}
        onEnded={() => setIsPlaying(false)}
        onClick={handlePlayPause}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Video Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={handlePlayPause}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <Play className="h-4 w-4 sm:h-5 sm:w-5 text-white ml-0.5" />
              )}
            </button>
            <button
              onClick={handleMute}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <Volume2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              )}
            </button>
          </div>
          <button
            onClick={handleFullscreen}
            className="p-1.5 sm:p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <Maximize className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && (
        <div
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={handlePlayPause}
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors">
            <Play className="h-6 w-6 sm:h-8 sm:w-8 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

const Pricing = () => {
  const [tokenAmount, setTokenAmount] = useState(40);
  const [creditType, setCreditType] = useState('mcq'); // 'mcq' or 'coding'
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success', 'error', null
  const [statusMessage, setStatusMessage] = useState('');
  const { isAuthenticated, user } = useUserAuth();
  const navigate = useNavigate();

  const mcqPricePerToken = 10 / 40;
  const codingPricePerToken = 10 / 2;

  const pricePerToken = creditType === 'mcq' ? mcqPricePerToken : codingPricePerToken;
  const minTokens = creditType === 'mcq' ? 40 : 2;
  const maxTokens = creditType === 'mcq' ? 1000 : 50;
  const step = creditType === 'mcq' ? 40 : 2;

  const totalPrice = tokenAmount * pricePerToken;

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setTokenAmount(value);
  };

  const incrementTokens = () => {
    if (tokenAmount < maxTokens) {
      setTokenAmount(prev => Math.min(prev + step, maxTokens));
    }
  };

  const decrementTokens = () => {
    if (tokenAmount > minTokens) {
      setTokenAmount(prev => Math.max(prev - step, minTokens));
    }
  };

  /**
   * Handles the Razorpay payment flow
   * 1. Creates order on backend
   * 2. Opens Razorpay modal
   * 3. Verifies payment on successful completion
   */
  const handlePayment = useCallback(async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/pricing' }, tokenAmount, creditType } });
      return;
    }

    setIsProcessing(true);
    setPaymentStatus(null);
    setStatusMessage('');

    try {
      // Step 1: Create order on backend
      const amountInPaise = Math.round(totalPrice * 100); // Convert to paise

      const orderResponse = await userApi.createPaymentOrder({
        amount: amountInPaise,
        creditType: creditType,
        creditAmount: tokenAmount,
      });

      if (!orderResponse.orderId) {
        throw new Error('Failed to create payment order');
      }

      // Step 2: Configure Razorpay options
      const options = {
        key: orderResponse.keyId || config.razorpayKeyId, // Razorpay Key ID
        amount: orderResponse.amount,
        currency: orderResponse.currency || 'INR',
        name: 'NeoPass',
        description: `${tokenAmount} ${creditType.toUpperCase()} Credits`,
        order_id: orderResponse.orderId,
        prefill: {
          name: user?.username || '',
          email: user?.email || '',
        },
        theme: {
          color: '#FBBF24', // Accent color
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            setPaymentStatus('error');
            setStatusMessage('Payment cancelled');
          },
        },
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            const verificationResponse = await userApi.verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              creditType: creditType,
              creditAmount: tokenAmount,
            });

            if (verificationResponse.success) {
              setPaymentStatus('success');
              setStatusMessage(
                `Payment successful! ${verificationResponse.creditsAdded} ${creditType.toUpperCase()} credits added. New balance: ${verificationResponse.newBalance}`
              );
            } else {
              setPaymentStatus('error');
              setStatusMessage(verificationResponse.message || 'Payment verification failed');
            }
          } catch (verifyError) {
            setPaymentStatus('error');
            setStatusMessage('Payment verification failed. Please contact support.');
            console.error('Verification error:', verifyError);
          } finally {
            setIsProcessing(false);
          }
        },
      };

      // Step 4: Open Razorpay modal
      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setIsProcessing(false);
        setPaymentStatus('error');
        setStatusMessage(
          response.error?.description || 'Payment failed. Please try again.'
        );
        console.error('Payment failed:', response.error);
      });
      razorpay.open();

    } catch (error) {
      setIsProcessing(false);
      setPaymentStatus('error');
      setStatusMessage(error.message || 'Failed to initiate payment. Please try again.');
      console.error('Payment error:', error);
    }
  }, [isAuthenticated, navigate, totalPrice, creditType, tokenAmount, user]);

  const handlePayClick = () => {
    handlePayment();
  };

  const features = [
    'AI-powered learning assistance',
    'Unlimited study queries per day (within credit limit)',
    'Instant concept explanations',
    'Step-by-step problem solving',
    'Exam preparation support',
    'Priority email support',
  ];

  const tokenPresets = creditType === 'mcq'
    ? [40, 80, 160, 400, 1000]
    : [2, 4, 10, 20, 50];

  const faqs = [
    {
      q: 'What is a credit?',
      a: 'A credit is a unit of usage for the AI learning assistant. Each query consumes credits based on the complexity of your question and the response generated.'
    },
    {
      q: 'Do credits expire?',
      a: 'No, your purchased credits never expire. Use them whenever you need learning assistance.'
    },
    {
      q: 'Are refunds available?',
      a: 'No, all credit purchases are final and non-refundable. Due to the digital nature of our service, we cannot offer refunds once credits are delivered. Please review our Refund Policy before purchasing.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">CREDIT PACKS</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-white mb-6 tracking-tight">
              Simple, transparent pricing
            </h1>
            <p className="text-white/50 text-lg">
              Purchase learning credits for your study sessions. No subscriptions, no hidden fees.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section className="pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left - Calculator */}
            <div className="border border-white/10 p-8 md:p-12">
              <h2 className="text-2xl font-light text-white mb-2">Learning Credits</h2>
              <p className="text-white/50 mb-8">Select the type of credits for your study sessions</p>

              {/* Credit Type Selector */}
              <div className="flex gap-4 mb-10">
                <button
                  onClick={() => { setCreditType('mcq'); setTokenAmount(40); }}
                  className={`flex-1 p-6 border transition-all ${creditType === 'mcq'
                      ? 'border-accent bg-accent\/10'
                      : 'border-white/10 hover:border-white/30'
                    }`}
                >
                  <p className={`text-lg font-medium ${creditType === 'mcq' ? 'text-accent' : 'text-white/70'}`}>MCQ Credits</p>
                  <p className="text-sm text-white/40 mt-1">₹0.25 per credit</p>
                </button>
                <button
                  onClick={() => { setCreditType('coding'); setTokenAmount(2); }}
                  className={`flex-1 p-6 border transition-all ${creditType === 'coding'
                      ? 'border-blue-400 bg-blue-400/10'
                      : 'border-white/10 hover:border-white/30'
                    }`}
                >
                  <p className={`text-lg font-medium ${creditType === 'coding' ? 'text-blue-400' : 'text-white/70'}`}>Coding Credits</p>
                  <p className="text-sm text-white/40 mt-1">₹5 per credit</p>
                </button>
              </div>

              {/* Token Amount Display */}
              <div className="text-center mb-10">
                <div className={`text-7xl font-light mb-2 ${creditType === 'mcq' ? 'text-accent' : 'text-blue-400'}`}>{tokenAmount}</div>
                <p className="text-white/40 text-sm font-mono tracking-widest">{creditType.toUpperCase()} CREDITS</p>
              </div>

              {/* Slider with +/- buttons */}
              <div className="flex items-center gap-4 mb-8">
                <button
                  onClick={decrementTokens}
                  disabled={tokenAmount <= minTokens}
                  className="p-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="flex-1 relative">
                  <input
                    type="range"
                    min={minTokens}
                    max={maxTokens}
                    step={step}
                    value={tokenAmount}
                    onChange={handleSliderChange}
                    className="w-full h-1 bg-white/10 appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, rgb(251, 191, 36) 0%, rgb(251, 191, 36) ${((tokenAmount - minTokens) / (maxTokens - minTokens)) * 100}%, rgba(255,255,255,0.1) ${((tokenAmount - minTokens) / (maxTokens - minTokens)) * 100}%, rgba(255,255,255,0.1) 100%)`
                    }}
                  />
                  <div className="flex justify-between text-xs text-white/40 mt-3 font-mono">
                    <span>{minTokens}</span>
                    <span>{maxTokens}</span>
                  </div>
                </div>
                <button
                  onClick={incrementTokens}
                  disabled={tokenAmount >= maxTokens}
                  className="p-3 border border-white/20 text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-10">
                {tokenPresets.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setTokenAmount(preset)}
                    className={`px-4 py-2 text-sm font-mono transition-colors ${tokenAmount === preset
                        ? 'bg-white text-black'
                        : 'border border-white/20 text-white/70 hover:text-white hover:border-white/40'
                      }`}
                  >
                    {preset}
                  </button>
                ))}
              </div>

              {/* Price Display */}
              <div className="bg-white/[0.02] border border-white/10 p-6 mb-8">
                <p className="text-white/40 text-xs font-mono tracking-widest mb-2">TOTAL PRICE</p>
                <div className="text-4xl font-light text-white mb-1">₹{totalPrice.toFixed(2)}</div>
                <p className="text-white/40 text-sm">₹{pricePerToken.toFixed(2)} per token</p>
              </div>

              {/* Warning */}
              <div className="p-4 border border-accent\/20 bg-accent\/5 mb-8">
                <p className="text-xs text-accent\/80">
                  ⚠️ All purchases are final and non-refundable. Password reset is not available. Please review our policies before purchasing.
                </p>
              </div>

              {/* Payment Status Message */}
              {paymentStatus && (
                <div className={`p-4 mb-6 border ${paymentStatus === 'success'
                    ? 'border-green-500/30 bg-green-500/10'
                    : 'border-red-500/30 bg-red-500/10'
                  }`}>
                  <p className={`text-sm ${paymentStatus === 'success' ? 'text-green-400' : 'text-red-400'
                    }`}>
                    {paymentStatus === 'success' ? '✓ ' : '✗ '}{statusMessage}
                  </p>
                </div>
              )}

              {/* CTA Button */}
              <button
                onClick={handlePayClick}
                disabled={isProcessing}
                className={`w-full px-6 py-4 font-medium transition-colors flex items-center justify-center gap-2 ${isProcessing
                    ? 'bg-white/50 text-black/50 cursor-not-allowed'
                    : 'bg-white text-black hover:bg-white/90'
                  }`}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : isAuthenticated ? (
                  <>
                    Proceed to Payment
                    <ChevronRight className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Login to Purchase
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </button>

              {!isAuthenticated && (
                <p className="mt-6 text-center text-sm text-white/50">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-white hover:text-accent transition-colors">
                    Sign up for free
                  </Link>
                </p>
              )}
            </div>

            {/* Right - Features */}
            <div>
              <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">INCLUDED</span>
              <h2 className="text-3xl font-light text-white mb-10">What you get</h2>
              <div className="space-y-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 border border-white/10 hover:border-white/20 transition-colors">
                    <Check className="h-5 w-5 text-accent flex-shrink-0" />
                    <span className="text-white/80">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          {/* How to Buy Tutorial */}
          <div className="mb-20">
            <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">TUTORIAL</span>
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              How to purchase credits
            </h2>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Video Player */}
              <div>
                <VideoPlayer
                  src={config.videos.buyCredits}
                  title="PURCHASE TUTORIAL"
                  id="pricing-buy-video"
                />
              </div>

              {/* Privacy Info */}
              <div>
                <div className="flex items-start gap-4 p-6 bg-white/[0.02] border border-white/10 mb-6">
                  <ShieldCheck className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2">100% Private & Secure</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Your payment is processed securely through Razorpay, India's trusted payment gateway. We never store your card details.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-white/[0.02] border border-white/10">
                  <div className="p-2 bg-accent/10 border border-accent/20 rounded">
                    <span className="text-accent text-lg">📱</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-white mb-2">Phone Number Verification</h3>
                    <p className="text-white/50 text-sm leading-relaxed">
                      Your phone number is only used for payment verification by Razorpay. We do not store or use it for any other purpose.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">FAQ</span>
          <h2 className="text-3xl md:text-4xl font-light text-white mb-12">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-3 gap-px bg-white/10">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-[#0a0a0a] p-8">
                <h3 className="text-lg font-medium text-white mb-4">{faq.q}</h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {faq.q === 'Are refunds available?' ? (
                    <>
                      No, all credit purchases are final and non-refundable. Due to the digital nature of our service, we cannot offer refunds. Please contact <a href="mailto:neopassbusiness@gmail.com" className="text-accent hover:text-accent-light">neopassbusiness@gmail.com</a> for technical issues only. See our full <Link to="/refund-policy" className="text-accent hover:text-accent-light">Refund Policy</Link>.
                    </>
                  ) : faq.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default Pricing;
